'use client';

import { Globe, Check } from 'lucide-react';
import { useI18n } from './i18n-provider';
import { locales, localeNames, type Locale } from '@/lib/i18n';
import { useState, useRef, useEffect } from 'react';

export function LanguageSwitcher({ variant = 'default' }: { variant?: 'default' | 'minimal' }) {
  const { locale, setLocale } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (newLocale: Locale) => {
    setLocale(newLocale);
    setIsOpen(false);
  };

  if (variant === 'minimal') {
    return (
      <div ref={ref} className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-[oklch(0.26_0.02_220)] transition-colors"
          aria-label="Switch language"
        >
          <Globe className="w-4 h-4" />
          <span className="hidden sm:inline">{localeNames[locale]}</span>
        </button>

        {isOpen && (
          <div className="absolute top-full mt-2 left-0 sm:right-0 min-w-[140px] rounded-xl bg-[oklch(0.20_0.015_220)] border border-[oklch(0.28_0.025_170)] shadow-lg shadow-black/30 overflow-hidden z-50">
            {locales.map((loc) => (
              <button
                key={loc}
                onClick={() => handleSelect(loc)}
                className={`w-full px-4 py-2.5 text-left text-sm flex items-center justify-between hover:bg-[oklch(0.26_0.02_220)] transition-colors ${
                  locale === loc ? 'text-[oklch(0.72_0.22_150)]' : 'text-foreground'
                }`}
              >
                <span>{localeNames[loc]}</span>
                {locale === loc && <Check className="w-4 h-4" />}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm border border-[oklch(0.28_0.025_170)] text-muted-foreground hover:text-foreground hover:border-[oklch(0.62_0.19_150)]/30 transition-colors"
        aria-label="Switch language"
      >
        <Globe className="w-4 h-4" />
        <span>{localeNames[locale]}</span>
      </button>

      {isOpen && (
        <div className="absolute top-full mt-2 right-0 min-w-[160px] rounded-xl bg-[oklch(0.20_0.015_220)] border border-[oklch(0.28_0.025_170)] shadow-lg shadow-black/30 overflow-hidden z-50">
          {locales.map((loc) => (
            <button
              key={loc}
              onClick={() => handleSelect(loc)}
              className={`w-full px-4 py-2.5 text-left text-sm flex items-center justify-between hover:bg-[oklch(0.26_0.02_220)] transition-colors ${
                locale === loc ? 'text-[oklch(0.72_0.22_150)]' : 'text-foreground'
              }`}
            >
              <span>{localeNames[loc]}</span>
              {locale === loc && <Check className="w-4 h-4" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
